import { useEffect, useMemo, useState } from "react";
import { Table } from "reactstrap";
import { useForm } from "react-hook-form";
import axios from "axios";

import regresaBienFecha from "../tools/regresaBienFecha";
import obtieneDatos from "../tools/obtieneDatosCookiesUsuario";
import comprueboPermisos from "../tools/comprueboPermisos";
import ModalInasistencia from "./Modal";

import "./ModuloDevuelveInasistenciasDocentes.css";

function ModuloDevuelveInasistenciasDocentes(props) {
  const Usuario = obtieneDatos();

  const [datosDocente, setDatosDocente] = useState(props.Docentes || []);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [inasistenciaSeleccionada, setInasistenciaSeleccionada] =
    useState(null);

  const [guardando, setGuardando] = useState(false);
  const [anulandoId, setAnulandoId] = useState(null);

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: {
      devolvioInasistencia: "",
      inciso: "",
      folio: "",
    },
  });

  /*
   * Actualiza la tabla cuando el componente padre
   * refresca la propiedad Docentes.
   */
  useEffect(() => {
    setDatosDocente(props.Docentes || []);
  }, [props.Docentes]);

  /*
   * Permiso para gestionar devoluciones.
   */
  const puedeGestionarDevoluciones = useMemo(() => {
    return !comprueboPermisos(
      Usuario?.funcion || "",
      "no gestiono devolucion ausencia docente"
    );
  }, [Usuario?.funcion]);

  /*
   * Fecha actual para limitar el input.
   */
  const fechaActual = useMemo(() => {
    return new Date().toISOString().split("T")[0];
  }, []);

  /*
   * Abre el modal de devolución.
   */
  const creaDevolucion = (inasistencia) => {
    setInasistenciaSeleccionada(inasistencia);

    reset({
      devolvioInasistencia: "",
      inciso: inasistencia?.inciso || "",
      folio: inasistencia?.folioInciso || "",
    });

    setModalAbierto(true);
  };

  /*
   * Cierra el modal y limpia el formulario.
   */
  const cerrarModal = () => {
    if (guardando) return;

    setModalAbierto(false);
    setInasistenciaSeleccionada(null);

    reset({
      devolvioInasistencia: "",
      inciso: "",
      folio: "",
    });
  };

  /*
   * Registra la devolución.
   */
  const registraCambios = async (valores) => {
    if (!inasistenciaSeleccionada?.id || guardando) return;

    try {
      setGuardando(true);

      const parametrosApasar = {
        folioInciso: `folio ${valores.folio} - Inc ${valores.inciso}`,
        devolvioInasistencia: valores.devolvioInasistencia,
      };

      const tramite = await axios.post(
        `${process.env.REACT_APP_BASEURL}docentes/creaDevuelve/${inasistenciaSeleccionada.id}`,
        parametrosApasar
      );

      if (tramite) {
        alert("Registrada la devolución");

        cerrarModal();
        props.refresco("dale");
      }
    } catch (error) {
      console.error("Error registrando la devolución:", error);

      alert(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "No se pudo registrar la devolución."
      );
    } finally {
      setGuardando(false);
    }
  };

  /*
   * Anula una devolución ya registrada.
   */
  const anula = async (inasistencia) => {
    if (!inasistencia?.id || anulandoId !== null) return;

    try {
      setAnulandoId(inasistencia.id);

      const parametrosApasar = {
        folioInciso: null,
        devolvioInasistencia: null,
      };

      const tramite = await axios.post(
        `${process.env.REACT_APP_BASEURL}docentes/creaDevuelve/${inasistencia.id}`,
        parametrosApasar
      );

      if (!tramite) return;

      const valorLog = {
        idInasistencia: inasistencia.id,
        idUsuario: Usuario?.id,
        cambio: inasistencia.devolvioInasistencia,
        folioInciso: inasistencia.folioInciso,
      };

      const creoLogDevuelvo = await axios.post(
        `${process.env.REACT_APP_BASEURL}docentes/creaLogDevuelve/`,
        valorLog
      );

      if (creoLogDevuelvo) {
        alert("Anulada la devolución");
        props.refresco("dale");
      }
    } catch (error) {
      console.error("Error anulando la devolución:", error);

      alert(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "No se pudo anular la devolución."
      );
    } finally {
      setAnulandoId(null);
    }
  };

  const ordenarPor = (campo) => {
    if (typeof props.myID === "function") {
      props.myID(campo);
    }
  };

  const columnas = [
    { campo: "id", titulo: "Ctrol" },
    { campo: "docente", titulo: "Docente" },
    { campo: "motivo", titulo: "Motivo" },
    { campo: "curso", titulo: "Curso" },
    { campo: null, titulo: "Escuela" },
    { campo: "materia", titulo: "Materia" },
    { campo: "division", titulo: "Div." },
    { campo: "sede", titulo: "Sede" },
    { campo: "fechaInasistencia", titulo: "Fecha" },
    { campo: "devolvioInasistencia", titulo: "Devolución" },
  ];

  return (
    <>
      <section className="inasistencias-card">
        <header className="inasistencias-card__header">
          <div>
            <span className="inasistencias-card__eyebrow">
              Gestión de docentes
            </span>

            <h3 className="inasistencias-card__title">
              Inasistencias docentes
            </h3>

            <p className="inasistencias-card__description">
              Registro y control de devolución de horas.
            </p>
          </div>

          <div className="inasistencias-card__contador">
            <span>Total</span>
            <strong>{datosDocente.length}</strong>
          </div>
        </header>

        <div className="inasistencias-table-container">
          <Table
            responsive
            hover
            borderless
            className="inasistencias-table"
          >
            <thead>
              <tr>
                {columnas.map((columna) => (
                  <th
                    key={columna.titulo}
                    className={
                      columna.campo
                        ? "inasistencias-table__sortable"
                        : undefined
                    }
                    onClick={() =>
                      columna.campo && ordenarPor(columna.campo)
                    }
                    title={
                      columna.campo
                        ? `Ordenar por ${columna.titulo}`
                        : undefined
                    }
                  >
                    <span>{columna.titulo}</span>

                    {columna.campo && (
                      <span className="inasistencias-table__sort-icon">
                        ↕
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {datosDocente.length > 0 ? (
                datosDocente.map((inasistencia) => {
                  const anulando = anulandoId === inasistencia.id;

                  return (
                    <tr key={inasistencia.id}>
                      <td>
                        <span className="inasistencias-id">
                          #{inasistencia.id}
                        </span>
                      </td>

                      <td>
                        <strong className="inasistencias-docente">
                          {inasistencia.docente || "Sin informar"}
                        </strong>
                      </td>

                      <td>{inasistencia.motivo || "—"}</td>
                      <td>{inasistencia.curso || "—"}</td>
                      <td>{inasistencia.escuela || "—"}</td>
                      <td>{inasistencia.materia || "—"}</td>

                      <td className="inasistencias-table__center">
                        {inasistencia.division || "—"}
                      </td>

                      <td>{inasistencia.sedeCursada || "—"}</td>

                      <td className="inasistencias-table__center">
                        <span className="inasistencias-fecha">
                          {regresaBienFecha(
                            inasistencia.fechaInasistencia
                          )}
                        </span>
                      </td>

                      <td className="inasistencias-table__center">
                        {puedeGestionarDevoluciones ? (
                          inasistencia.devolvioInasistencia == null ? (
                            <button
                              type="button"
                              className="inasistencias-button inasistencias-button--primary"
                              onClick={() =>
                                creaDevolucion(inasistencia)
                              }
                            >
                              <span className="inasistencias-button__icon">
                                ✓
                              </span>

                              Registrar devolución
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="inasistencias-devolucion"
                              onClick={() => anula(inasistencia)}
                              disabled={anulando}
                              title="Presione para anular la devolución"
                            >
                              <span className="inasistencias-devolucion__icon">
                                ✓
                              </span>

                              <span className="inasistencias-devolucion__text">
                                <strong>
                                  {anulando
                                    ? "Anulando..."
                                    : `Devuelto el ${regresaBienFecha(
                                        inasistencia.devolvioInasistencia
                                      )}`}
                                </strong>

                                <small>
                                  {inasistencia.folioInciso ||
                                    "Sin folio informado"}
                                </small>
                              </span>
                            </button>
                          )
                        ) : inasistencia.devolvioInasistencia ? (
                          <div className="inasistencias-devolucion inasistencias-devolucion--readonly">
                            <span className="inasistencias-devolucion__icon">
                              ✓
                            </span>

                            <span className="inasistencias-devolucion__text">
                              <strong>
                                Devuelto el{" "}
                                {regresaBienFecha(
                                  inasistencia.devolvioInasistencia
                                )}
                              </strong>

                              <small>
                                {inasistencia.folioInciso ||
                                  "Sin folio informado"}
                              </small>
                            </span>
                          </div>
                        ) : (
                          <span className="inasistencias-sin-devolucion">
                            Sin devolución
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10}>
                    <div className="inasistencias-empty">
                      <div className="inasistencias-empty__icon">✓</div>

                      <h4>No hay inasistencias para mostrar</h4>

                      <p>
                        Cuando existan registros aparecerán en esta tabla.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </section>

      {modalAbierto && inasistenciaSeleccionada && (
        <ModalInasistencia
          isOpen={modalAbierto}
          onClose={cerrarModal}
        >
          <div className="devolucion-modal">
            <div className="devolucion-modal__header">
              <span className="devolucion-modal__eyebrow">
                Devolución de horas
              </span>

              <h3>Registrar devolución</h3>

              <p>
                Complete los datos correspondientes a la devolución de
                horas del docente.
              </p>
            </div>

            <div className="devolucion-resumen">
              <div className="devolucion-resumen__item devolucion-resumen__item--full">
                <span>Docente</span>
                <strong>{inasistenciaSeleccionada.docente}</strong>
              </div>

              <div className="devolucion-resumen__item">
                <span>Materia</span>
                <strong>{inasistenciaSeleccionada.materia}</strong>
              </div>

              <div className="devolucion-resumen__item">
                <span>División</span>
                <strong>
                  {inasistenciaSeleccionada.division}° división
                </strong>
              </div>

              <div className="devolucion-resumen__item">
                <span>Escuela</span>
                <strong>{inasistenciaSeleccionada.escuela}</strong>
              </div>

              <div className="devolucion-resumen__item">
                <span>Sede</span>
                <strong>{inasistenciaSeleccionada.sedeCursada}</strong>
              </div>

              <div className="devolucion-resumen__item devolucion-resumen__item--full">
                <span>Fecha de la inasistencia</span>
                <strong>
                  {regresaBienFecha(
                    inasistenciaSeleccionada.fechaInasistencia
                  )}
                </strong>
              </div>
            </div>

            <form
              className="devolucion-form"
              onSubmit={handleSubmit(registraCambios)}
            >
              <div className="devolucion-form__grid">
                <div className="devolucion-form__group devolucion-form__group--full">
                  <label htmlFor="devolvioInasistencia">
                    Fecha de devolución
                    <span className="devolucion-form__required">*</span>
                  </label>

                  <input
                    type="date"
                    id="devolvioInasistencia"
                    className={
                      errors.devolvioInasistencia
                        ? "devolucion-form__input devolucion-form__input--error"
                        : "devolucion-form__input"
                    }
                    min={inasistenciaSeleccionada.fechaInasistencia}
                    max={fechaActual}
                    {...register("devolvioInasistencia", {
                      required: "Debe seleccionar la fecha de devolución.",
                    })}
                  />

                  {errors.devolvioInasistencia && (
                    <span className="devolucion-form__error">
                      {errors.devolvioInasistencia.message}
                    </span>
                  )}
                </div>

                <div className="devolucion-form__group">
                  <label htmlFor="folio">
                    Folio
                    <span className="devolucion-form__required">*</span>
                  </label>

                  <input
                    type="text"
                    id="folio"
                    placeholder="Ejemplo: 125"
                    autoComplete="off"
                    className={
                      errors.folio
                        ? "devolucion-form__input devolucion-form__input--error"
                        : "devolucion-form__input"
                    }
                    {...register("folio", {
                      required: "Debe ingresar el folio.",
                      setValueAs: (value) => value?.trim(),
                    })}
                  />

                  {errors.folio && (
                    <span className="devolucion-form__error">
                      {errors.folio.message}
                    </span>
                  )}
                </div>

                <div className="devolucion-form__group">
                  <label htmlFor="inciso">
                    Inciso
                    <span className="devolucion-form__required">*</span>
                  </label>

                  <input
                    type="text"
                    id="inciso"
                    placeholder="Ejemplo: A"
                    autoComplete="off"
                    className={
                      errors.inciso
                        ? "devolucion-form__input devolucion-form__input--error"
                        : "devolucion-form__input"
                    }
                    {...register("inciso", {
                      required: "Debe ingresar el inciso.",
                      setValueAs: (value) => value?.trim(),
                    })}
                  />

                  {errors.inciso && (
                    <span className="devolucion-form__error">
                      {errors.inciso.message}
                    </span>
                  )}
                </div>
              </div>

              <div className="devolucion-form__actions">
                <button
                  type="button"
                  className="inasistencias-button inasistencias-button--secondary"
                  onClick={cerrarModal}
                  disabled={guardando}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="inasistencias-button inasistencias-button--primary inasistencias-button--large"
                  disabled={guardando}
                >
                  {guardando
                    ? "Registrando..."
                    : "Confirmar devolución"}
                </button>
              </div>
            </form>
          </div>
        </ModalInasistencia>
      )}
    </>
  );
}

export default ModuloDevuelveInasistenciasDocentes;